// src/services/reportsAPI.ts
// ✅ LOGGING: Complete migration to centralized logging system
// ✅ ENVIRONMENT: Using environment configuration

import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';

// ✅ LOGGING: Create dedicated logger for Reports API
const reportsAPILogger = logger.createServiceLogger('ReportsAPI');

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Get auth headers with JWT token
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const timer = logger.startTimer('ReportsAPI', 'getAuthHeaders');
  
  try {
    reportsAPILogger.debug('Getting authentication headers for reports API');
    
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (!token) {
      reportsAPILogger.error('No authentication token available for reports');
      throw new Error('No authentication token available');
    }
    
    reportsAPILogger.debug('Authentication headers obtained successfully');
    timer();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  } catch (error) {
    timer();
    reportsAPILogger.error('Error getting auth headers for reports', {
      error: error instanceof Error ? error.message : 'Unknown error'
    }, error instanceof Error ? error : undefined);
    throw new Error('Authentication required');
  }
};

// Handle API responses
const handleResponse = async <T>(response: Response): Promise<T> => {
  const timer = logger.startTimer('ReportsAPI', 'handleResponse');
  
  try {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      reportsAPILogger.error('Reports API response error', {
        status: response.status,
        statusText: response.statusText,
        errorData
      });
      timer();
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: ApiResponse<T> = await response.json();
    
    if (!data.success && data.error) {
      reportsAPILogger.error('Reports API business logic error', {
        error: data.error,
        message: data.message
      });
      timer();
      throw new Error(data.error);
    }
    
    reportsAPILogger.debug('Reports API response handled successfully', {
      hasData: !!data.data,
      success: data.success
    });
    
    timer();
    return data.data || data as T;
  } catch (error) {
    timer();
    throw error;
  }
};

// Types for API responses
interface CategoryConfig {
  configuration: {
    values: Array<{
      categoryId: string;
      name: string;
      parentCategoryId?: string;
    }>;
  };
}

interface OfficersListResponse {
  users: Array<{
    userId: string;
    firstName: string;
    lastName: string;
    badgeNumber: string;
    rank: string;
  }>;
}

// Get API base URL
const API_BASE_URL = getApiBaseUrl();

export const reportsAPI = {
  // Generate inventory worksheet data
  generateInventoryWorksheet: async (filters: {
    categoryId?: string;
    subcategoryId?: string;
    includeAssigned?: boolean;
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'generateInventoryWorksheet');
    
    reportsAPILogger.info('Generating inventory worksheet', {
      categoryId: filters.categoryId || null,
      subcategoryId: filters.subcategoryId || null,
      includeAssigned: filters.includeAssigned ?? false,
      hasFilters: !!(filters.categoryId || filters.subcategoryId)
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      if (filters.categoryId) queryParams.set('categoryId', filters.categoryId);
      if (filters.subcategoryId) queryParams.set('subcategoryId', filters.subcategoryId);
      if (filters.includeAssigned !== undefined) queryParams.set('includeAssigned', String(filters.includeAssigned));
      
      const response = await fetch(
        `${API_BASE_URL}/reports/inventory-worksheet?${queryParams}`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse(response);
      reportsAPILogger.info('Inventory worksheet generated successfully', {
        categoryId: filters.categoryId || null,
        includeAssigned: filters.includeAssigned
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to generate inventory worksheet', {
        filters,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Generate officer equipment report
  generateOfficerEquipmentReport: async (filters: {
    officerId?: string;
    status?: string;
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'generateOfficerEquipmentReport');
    
    reportsAPILogger.info('Generating officer equipment report', {
      officerId: filters.officerId || 'all officers',
      status: filters.status || 'all statuses',
      hasFilters: !!(filters.officerId || filters.status)
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      if (filters.officerId) queryParams.set('officerId', filters.officerId);
      if (filters.status) queryParams.set('status', filters.status);
      
      const response = await fetch(
        `${API_BASE_URL}/reports/officer-equipment?${queryParams}`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse(response);
      reportsAPILogger.info('Officer equipment report generated successfully', {
        officerId: filters.officerId || 'all',
        status: filters.status || 'all'
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to generate officer equipment report', {
        filters,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Generate annual assignment report
  generateAnnualAssignmentReport: async (filters: {
    startDate: string;
    endDate: string;
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'generateAnnualAssignmentReport');
    
    reportsAPILogger.info('Generating annual assignment report', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      dateRange: `${filters.startDate} to ${filters.endDate}`
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      queryParams.set('startDate', filters.startDate);
      queryParams.set('endDate', filters.endDate);
      
      const response = await fetch(
        `${API_BASE_URL}/reports/annual-assignments?${queryParams}`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse(response);
      reportsAPILogger.info('Annual assignment report generated successfully', {
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to generate annual assignment report', {
        filters,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Generate budget analysis report
  generateBudgetAnalysisReport: async (filters: {
    startDate: string;
    endDate: string;
    compareWithPreviousPeriod?: boolean;
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'generateBudgetAnalysisReport');
    
    reportsAPILogger.info('Starting budget analysis report generation', {
      startDate: filters.startDate,
      endDate: filters.endDate,
      compareWithPreviousPeriod: filters.compareWithPreviousPeriod || false,
      dateRange: `${filters.startDate} to ${filters.endDate}`
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      queryParams.set('startDate', filters.startDate);
      queryParams.set('endDate', filters.endDate);
      if (filters.compareWithPreviousPeriod) {
        queryParams.set('compareWithPreviousPeriod', 'true');
      }
      
      const requestUrl = `${API_BASE_URL}/reports/budget-analysis?${queryParams}`;
      reportsAPILogger.debug('Budget analysis API request details', {
        url: requestUrl,
        method: 'GET',
        hasComparison: !!filters.compareWithPreviousPeriod
      });
      
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers,
      });
      
      reportsAPILogger.info('Budget analysis API response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        reportsAPILogger.error('Budget analysis API request failed', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500) // Limit error text length
        });
        timer();
        throw new Error(`Budget Analysis failed: ${response.status} - ${errorText}`);
      }
      
      const result = await handleResponse(response);
      reportsAPILogger.info('Budget analysis report generated successfully', {
        startDate: filters.startDate,
        endDate: filters.endDate,
        compareWithPreviousPeriod: filters.compareWithPreviousPeriod
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Budget analysis report generation failed', {
        filters,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Generate equipment audit report
  generateEquipmentAuditReport: async (filters: {
    reportType: 'FIREARMS' | 'SERIALIZED_ALL' | 'BODY_ARMOR';
    includeHistory?: boolean;
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'generateEquipmentAuditReport');
    
    reportsAPILogger.info('Generating equipment audit report', {
      reportType: filters.reportType,
      includeHistory: filters.includeHistory || false
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      queryParams.set('reportType', filters.reportType);
      if (filters.includeHistory) queryParams.set('includeHistory', 'true');
      
      const response = await fetch(
        `${API_BASE_URL}/reports/equipment-audit?${queryParams}`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse(response);
      reportsAPILogger.info('Equipment audit report generated successfully', {
        reportType: filters.reportType,
        includeHistory: filters.includeHistory
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to generate equipment audit report', {
        filters,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Generate expiration report
  generateExpirationReport: async (filters: {
    daysAhead?: number;
    expirationTypes?: string[];
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'generateExpirationReport');
    
    reportsAPILogger.info('Generating expiration report', {
      daysAhead: filters.daysAhead || 30,
      expirationTypes: filters.expirationTypes || [],
      expirationTypeCount: filters.expirationTypes?.length || 0
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      if (filters.daysAhead) queryParams.set('daysAhead', String(filters.daysAhead));
      if (filters.expirationTypes?.length) {
        filters.expirationTypes.forEach(type => queryParams.append('expirationTypes', type));
      }
      
      const response = await fetch(
        `${API_BASE_URL}/reports/expiration-report?${queryParams}`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse(response);
      reportsAPILogger.info('Expiration report generated successfully', {
        daysAhead: filters.daysAhead,
        expirationTypeCount: filters.expirationTypes?.length || 0
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to generate expiration report', {
        filters,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Generate and download report in specified format
  downloadReport: async (reportRequest: {
    type: string;
    format: 'PDF' | 'EXCEL' | 'CSV';
    filters: any;
    options?: any;
  }) => {
    const timer = logger.startTimer('ReportsAPI', 'downloadReport');
    
    reportsAPILogger.info('Starting report download', {
      type: reportRequest.type,
      format: reportRequest.format,
      hasFilters: !!reportRequest.filters,
      hasOptions: !!reportRequest.options,
      filterKeys: reportRequest.filters ? Object.keys(reportRequest.filters) : []
    });
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(
        `${API_BASE_URL}/reports/download`, 
        {
          method: 'POST',
          headers,
          body: JSON.stringify(reportRequest),
        }
      );
      
      if (!response.ok) {
        reportsAPILogger.error('Report download failed', {
          type: reportRequest.type,
          format: reportRequest.format,
          status: response.status,
          statusText: response.statusText
        });
        timer();
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
      const blob = response.blob();
      reportsAPILogger.info('Report download completed successfully', {
        type: reportRequest.type,
        format: reportRequest.format
      });
      
      timer();
      return blob;
    } catch (error) {
      timer();
      reportsAPILogger.error('Report download error', {
        type: reportRequest.type,
        format: reportRequest.format,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Get available categories for filtering
  getReportCategories: async (): Promise<CategoryConfig> => {
    const timer = logger.startTimer('ReportsAPI', 'getReportCategories');
    
    reportsAPILogger.info('Fetching report categories');
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(
        `${API_BASE_URL}/tenant-config/inventory.categories`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse<CategoryConfig>(response);
      reportsAPILogger.info('Report categories fetched successfully', {
        categoryCount: result.configuration?.values?.length || 0
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to fetch report categories', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },

  // Get officers list for officer reports
  getOfficersList: async (): Promise<OfficersListResponse> => {
    const timer = logger.startTimer('ReportsAPI', 'getOfficersList');
    
    reportsAPILogger.info('Fetching officers list for reports');
    
    try {
      const headers = await getAuthHeaders();
      
      const response = await fetch(
        `${API_BASE_URL}/users?status=Active`, 
        {
          method: 'GET',
          headers,
        }
      );
      
      const result = await handleResponse<OfficersListResponse>(response);
      reportsAPILogger.info('Officers list fetched successfully', {
        officerCount: result.users?.length || 0
      });
      
      timer();
      return result;
    } catch (error) {
      timer();
      reportsAPILogger.error('Failed to fetch officers list', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  },
};

export default reportsAPI;
